import { Callback } from '../../../types';
import type { ConnectBehavior } from '..';
import EventEmitter from 'events';
import { isMobile } from '../../../utils';
import Video, { ConnectOptions, LocalDataTrack, LocalTrack, Room } from 'twilio-video';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  captureTwilioConnectFailureDiagnostic,
  captureTwilioRoomNotFoundDiagnostic,
  captureTwilioSignalingClosedDiagnostic,
  recordTwilioConnectAttempt,
} from '../../../logging/twilioDiagnostics';

// @ts-ignore
window.TwilioVideo = Video;

const ROOM_NOT_FOUND_ERROR_CODE = 53106;

const getUniqueTracks = (tracks: Array<LocalTrack | undefined>) => {
  const uniqueTracks = new Set<LocalTrack>();

  tracks.forEach((track) => {
    if (track) {
      uniqueTracks.add(track);
    }
  });

  return Array.from(uniqueTracks);
};

export default function useRoom(localTracks: LocalTrack[], onError: Callback, options?: ConnectOptions) {
  const [room, setRoom] = useState<Room>(new EventEmitter() as Room);
  const [isConnecting, setIsConnecting] = useState(false);
  const localTracksRef = useRef<LocalTrack[]>([]);

  useEffect(() => {
    // It can take a moment for Video.connect to connect to a room. During this time, the user may have enabled or disabled their
    // local audio or video tracks. If this happens, we store the localTracks in this ref, so that they are correctly published
    // once the user is connected to the room.
    localTracksRef.current = localTracks;
  }, [localTracks]);

  const connect = useCallback(
    (token: any, localDataTrack: LocalDataTrack, behavior: ConnectBehavior = {}) => {
      setIsConnecting(true);
      const twilioEventListener = new EventEmitter();
      const connectOptions = {
        ...options,
        // Twilio still supports this deprecated eventListener hook and it exposes the
        // signaling close reason that is lost in the SDK's console output.
        eventListener: twilioEventListener,
        tracks: getUniqueTracks([...localTracksRef.current, localDataTrack]),
      } as ConnectOptions & {
        eventListener: EventEmitter;
      };

      twilioEventListener.on('event', (event) => {
        captureTwilioSignalingClosedDiagnostic(event);
      });

      recordTwilioConnectAttempt(typeof token === 'string' ? token : null);

      return Video.connect(token, {
        ...(connectOptions as ConnectOptions),
      }).then(
        (newRoom) => {
          setRoom(newRoom);
          const disconnect = () => newRoom.disconnect();

          newRoom.once('disconnected', () => {
            setTimeout(() => setRoom(new EventEmitter() as Room));
            window.removeEventListener('unload', disconnect);
            if (isMobile) {
              window.removeEventListener('pagehide', disconnect);
            }
          });

          // @ts-ignore
          window.twilioRoom = newRoom;

          setIsConnecting(false);

          window.addEventListener('unload', disconnect);
          if (isMobile) {
            window.addEventListener('pagehide', disconnect);
          }

          return newRoom; // ✅ THIS is what enables `then(room => ...)` in PractitionerPage
        },
        (error) => {
          const isRoomNotFound = error?.code === ROOM_NOT_FOUND_ERROR_CODE;
          const shouldSuppressGlobalError = isRoomNotFound || (behavior.suppressErrorCodes ?? []).includes(error?.code);

          // Capture 53106 as a tagged warning so we can root-cause it in production
          // without re-opening the global error dialog. Other connect failures
          // continue to land in Sentry as exceptions via the existing diagnostic.
          if (isRoomNotFound) {
            captureTwilioRoomNotFoundDiagnostic(
              error,
              { roomName: null, roomSid: null, roomState: null },
              typeof token === 'string' ? token : null
            );
          } else {
            captureTwilioConnectFailureDiagnostic(error, {
              roomName: null,
              roomSid: null,
              roomState: null,
            });
          }

          if (!shouldSuppressGlobalError) {
            onError(error);
          }
          setIsConnecting(false);
          throw error; // Make sure to rethrow the error for the caller
        }
      );
    },
    [options, onError]
  );

  return { room, isConnecting, connect };
}
